import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Trash2 } from 'lucide-react';
import { typography } from '../../styles/typography';
import { Card, PrimaryButton, SearchBar } from '../ui';
import { locationQrApi } from './locationQrApi';
import {
  filterQrCodes,
  getQrLocation,
  getQrName,
  getQrTypeLabel,
} from './qrData';

const ActionIconButton = ({ icon: Icon, label, onClick, disabled, colorClass, spinIcon }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className={`flex h-9 w-9 items-center justify-center rounded-lg text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 ${colorClass}`}
  >
    <Icon className={`w-4 h-4 ${spinIcon ? 'animate-spin' : ''}`} aria-hidden />
  </button>
);

const QRManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const query = debouncedSearch || undefined;
      const data = query
        ? await locationQrApi.list({ search: query, limit: 100 })
        : await locationQrApi.list({ limit: 100 });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load location QR codes');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const filteredItems = debouncedSearch ? items : filterQrCodes(items, searchTerm);

  const handleDelete = async (e, qr) => {
    e.stopPropagation();
    const name = getQrName(qr);
    const confirmed = window.confirm(`Delete "${name}"? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingId(qr.qr_id);
    try {
      await locationQrApi.delete(qr.qr_id);
      await loadItems();
    } catch (err) {
      alert(err.message || 'Failed to delete QR code');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Community QR management
        </h3>
        <PrimaryButton
          onClick={() => navigate('/qr-management/create')}
          className="!w-auto min-w-[160px] !h-12 !text-base"
        >
          Create QR
        </PrimaryButton>
      </div>

      <SearchBar
        placeholder="search by qr name, location"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        iconType="search"
        className="w-full max-w-2xl"
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12 px-4">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={loadItems}
              className="px-4 py-2 rounded-lg bg-[#6BABA0] text-white hover:opacity-90"
            >
              Retry
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 px-4 text-gray-500 font-['Open_Sans']">
            {items.length === 0
              ? 'No location QR codes yet. Create your first QR code.'
              : 'No QR codes match your search'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-zinc-200 bg-gray-50/80">
                  {['QR name', 'Type', 'Location', 'Status', 'Actions'].map(col => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 font-['Open_Sans']"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(qr => (
                  <tr
                    key={qr.qr_id}
                    className="border-b border-zinc-100 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-black font-['Open_Sans']">
                      {getQrName(qr)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-['Open_Sans']">
                      {getQrTypeLabel(qr)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-['Open_Sans']">
                      {getQrLocation(qr)}
                    </td>
                    <td className="px-4 py-3 text-sm font-['Open_Sans']">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          qr.status === 'inactive'
                            ? 'bg-gray-200 text-gray-700'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {qr.status === 'inactive' ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ActionIconButton
                        icon={deletingId === qr.qr_id ? Loader2 : Trash2}
                        label="Delete QR"
                        onClick={e => handleDelete(e, qr)}
                        disabled={deletingId === qr.qr_id}
                        spinIcon={deletingId === qr.qr_id}
                        colorClass="bg-red-500 hover:bg-red-600"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default QRManagement;
